<template>
  <div class="task-create text-dark">
    <DModal @close="close" :show-close="false">
      <div class="task-create__modal" slot="insertComponent">
        <div class="task-create__header" :class="{'task-create__header--create-subject': canSchedule}"
             :style="{'backgroundColor': color}">
          <h3>
            <span v-if="name.length < 1">{{ i18n[locale]['create_task'] }}</span>
            <span v-else>{{ name }}</span>
          </h3>
          <div class="task-create__close icon__bg-circle" @click="close">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <form id="task-create" class="task-create__body" @submit.prevent>
          <div class="task-create__form">
            <div class="input-group__flex">
            <div class="input-group input-group__title">
              <label>{{ i18n[locale]['task_title'] }}</label>
              <input type="text"
                     class="d-input"
                     v-model="name"
                     :placeholder="i18n[locale]['ex_task']"
                     @keyup="removeErrors('name')"
                     ref="name"
                     :class="{'input--error': formErrors['name']}">
              <p class="input-error" v-if="formErrors['name']">{{ formErrors['name'][locale] }}</p>
            </div>
              <div class="input-group input-group__preparation" v-if="isArbeidstidsplanActivated">
                <label>Årsramme</label>
                <input type="number" class="d-input" v-model="preparation"/>
              </div>
            </div>
            <div class="input-group">
              <label><span class="icon-user"></span> {{ i18n[locale]['add_user'] }}</label>
              <DSelectCalendarIds itemType="USER"
                                  :preSelectedItems="calendars"
                                  :showTags="true"
                                  :itemsThatCantBeDeleted="usersWithEvents"
                                  @update="setSelectedUsers">
              </DSelectCalendarIds>
            </div>
            <div class="input-group" v-if="!editTask">
              <label>{{ i18n[locale]['choose_type'] }}</label>
              <SubjectFormSubjectType calendar-type="user"
                                      :subject-type="subjectType"
                                      @change="setSubjectType"
                                      :isInTasks="true">
              </SubjectFormSubjectType>
            </div>
            <div class="input-group">
              <label>{{ i18n[locale]['hours_as'] }} <span class="pill pill--grey hint--top-right"
                                     aria-label="Vælg briktype for at ændre">{{ hoursType }}</span> </label>
              <div class="input-group__flex">
                <div style="width: 50%">
                  <input type="number" class="d-input" ref="inputLessonCount" v-model.number="hours"
                         @focus="selectAllOnFocus($event)"
                         @blur="removeErrors('hours')"
                         :class="{'input--error': formErrors['hours']}">
                  <p class="input-error" v-if="formErrors['hours']">{{ formErrors['hours'][locale] }}</p>
                </div>
              </div>
            </div>
            <div class="input-group" v-if="!editTask">
              <div class="create-as-subject">
                <div class="create-as-subject__inner" @click.stop="canSchedule = !canSchedule">
                  <AppToggle @changed="setIsCreateAsSubject" :propOn="canSchedule" :allowChanges="false"></AppToggle>
                  <label style="margin-bottom: 0">{{ i18n[locale]['can_be_scheduled'] }}</label>
                </div>
              </div>
            </div>
            <transition name="dropdown">
              <div v-if="canSchedule">
                <div class="input-group">
                  <label>{{ i18n[locale]['duration'] }}</label>
                  <input type="number"
                         class="d-input"
                         ref="inputLessonCount" v-model.number="duration"
                         style="width: 50%"
                         @focus="selectAllOnFocus($event)"
                         @blur="removeErrors('duration')"
                         :class="{'input--error': formErrors['duration']}">
                  <p class="input-error" v-if="formErrors['duration']">{{ formErrors['duration'][locale] }}</p>
                </div>
                <transition name="dropdown">
                  <div class="input-group" v-if="isCreateInOtherCalendarAnOption && !editTask">
                    <label>{{ i18n[locale]['create_in_calendar'] }}</label>
                    <SelectOtherCal :selectedCalendarId="selectedOtherCalendarId"
                                    @changed="setSelectedOtherCalendarId">
                    </SelectOtherCal>
                  </div>
                </transition>
                <div class="input-group" v-if="editTask && getOtherCalendarName(selectedOtherCalendarId)">
                  <label>{{ i18n[locale]['created_in_calendar'] }}</label>
                  <b>{{ getOtherCalendarName(selectedOtherCalendarId) }}</b>
                </div>
                <div class="input-group">
                  <label>{{ i18n[locale]['choose_color'] }}</label>
                  <color-picker @changedColor="setColor" :initialColor="color"></color-picker>
                </div>
              </div>
            </transition>
            <div class="input-group input-group__submit">
              <div class="input-group__submit__left">
                <template v-if="editTask">
                  <button v-if="usersWithEvents.length < 1"
                          class="hint--right bttn--warning"
                          aria-label="Slet opgaven"
                          @click="deleteTask">
                    <span class="icon-bin"></span>
                  </button>
                  <div class="hint--right" v-else :aria-label="i18n[locale]['cannot_be_deleted']">
                    <button class="bttn bttn--disabled">
                      <span class="icon-bin"></span>
                    </button>
                  </div>
                </template>
              </div>
              <div class="input-group__submit__right">
                <button class="marg-1-right do-bttn" @click="close">{{ i18n[locale]['cancel'] }}</button>
                <button class="do-bttn do-bttn--submit" :class="{'do-bttn--submit--loading': isSubmittingTask}" v-if="!editTask" @click="createTask">{{
                    i18n[locale]['create_task']
                  }}
                </button>
                <button class="do-bttn do-bttn--submit" :class="{'do-bttn--submit--loading': isSubmittingTask}" v-if="editTask" @click="updateTask">{{
                    i18n[locale]['update_task']
                  }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </DModal>
    <div class="bg-overlay animatedFast fadeIn"></div>
  </div>
</template>

<script>
import DSelectCalendarIds from '../DSelect/DSelectCalsIds'
import DModal from '../common/DModal'
import SubjectFormSubjectType from '../Subjects/SubjectFormSubjectType'
import AppToggle from '../common/AppToggle'
import SelectOtherCal from './SubjectsDistributionTasksCreateDropdown'
import DSelect from '../DSelect/DSelect'

const I18N = {
  da: {
    create_task: 'Opret opgave',
    update_task: 'Opdatér',
    task_title: 'Opgavens titel',
    ex_task: 'Eks. Tilsyn',
    choose_type: 'Vælg type',
    hours_as: 'Timer som',
    add_user: 'Tilføj personale',
    hours_in_school_year_per_person: 'Øvrig tid',
    teaching_hours: 'UV tid',
    cancel: 'Annullér',
    cannot_be_deleted: 'Kan ikke slettes, da der er skemalagte brikker',
    can_be_scheduled: 'Kan skemalægges',
    duration: 'NORMAL VARIGHED (I MINUTTER)',
    create_in_calendar: 'Opret i kalender',
    created_in_calendar: 'Oprettet i kalenderen',
    choose_color: 'Vælg farve',
    teaching: 'Undervisning',
    preparation: 'Forberedelse',
    additional_tasks: 'Øvrige Opgaver',
    available: 'Rådighedstimer',
    club: 'SFO tid',
    locale_pool: 'Lokalpulje'
  },
  nb: {
    create_task: 'Opprett oppgave',
    update_task: 'Oppdater',
    task_title: 'Navn på oppgave',
    ex_task: 'F.eks. Tilsyn',
    choose_type: 'Velg type',
    hours_as: 'Timer som',
    add_user: 'Legg til underviser(e)',
    hours_in_school_year_per_person: 'Øvrig tid',
    teaching_hours: 'Undervisningstid',
    cancel: 'Annuller',
    cannot_be_deleted: 'Kan ikke slettes, ettersom faget er timeplanlagt.',
    can_be_scheduled: 'Kan timeplanlegges',
    duration: 'STANDARDLENGDE (I MINUTTER)',
    create_in_calendar: 'Opprett i kalender',
    created_in_calendar: 'Opprettet i kalenderen',
    choose_color: 'Velg farge',
    teaching: 'Undervisning',
    preparation: 'Forberedelse',
    additional_tasks: 'Øvrige oppgaver',
    available: 'Rådighetstimer',
    club: 'SFO tid',
    locale_pool: 'Planleggingsdager'
  },
  en: {
    create_task: 'Add task',
    update_task: 'Update',
    task_title: 'Title',
    ex_task: 'eg. non teaching task',
    choose_type: 'Choose type',
    hours_as: 'Hours as',
    add_user: 'Add teacher(s)',
    hours_in_school_year_per_person: 'Additional time',
    teaching_hours: 'Teaching hours',
    cancel: 'Cancel',
    cannot_be_deleted: 'Cannot be deleted due to scheduled tasks.',
    can_be_scheduled: 'Can be scheduled',
    duration: 'STANDARD DURATION (IN MINUTES)',
    create_in_calendar: 'Create in calendar',
    created_in_calendar: 'Created in calendar',
    choose_color: 'Choose color',
    teaching: 'Teaching',
    preparation: 'Preparation',
    additional_tasks: 'Additional tasks',
    available: 'Available hours',
    club: 'Club hours',
    locale_pool: 'Custom pool'
  }
}

export default {
  components: {AppToggle, DModal, DSelectCalendarIds, SubjectFormSubjectType, SelectOtherCal, DSelect},
  props: {
    editTask: {
      default: null
    },
    activeFilter: {
      default: null
    }
  },
  data () {
    return {
      name: '',
      hours: 0,
      calendars: [],
      subjectType: 'additional_tasks',
      canSchedule: true,
      duration: 60,
      color: '#999999',
      selectedOtherCalendarId: null,
      formErrors: [],
      preparation: null
    }
  },
  computed: {
    isArbeidstidsplanActivated () {
      return this.$store.getters.isArbeidstidsplanActivated
    },
    i18n () {
      return I18N
    },
    schoolCountry () {
      return this.$store.getters.schoolCountry
    },
    isSchoolFromNorway () {
      return this.schoolCountry.toLowerCase() === 'no'
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    otherCalendars () {
      return this.$store.getters.otherCalendars
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    hoursType () {
      switch (this.subjectType) {
        case 'person_resource_visible':
        case 'person_resource':
        case 'subject':
          return this.i18n[this.locale]['teaching']
        case 'preparation':
          return this.i18n[this.locale]['preparation']
        case 'additional_tasks':
          return this.i18n[this.locale]['additional_tasks']
        case 'available':
          return this.i18n[this.locale]['available']
        case 'club':
          return this.i18n[this.locale]['club']
        case 'pool':
          return this.i18n[this.locale]['locale_pool']
        case 'with_kids':
          return 'Med børn'
        case 'without_kids':
          return 'Uden børn'
        default:
          return this.subjectType
      }
    },
    isCreateInOtherCalendarAnOption () {
      return this.subjectType === 'without_kids' || this.subjectType === 'preparation' || this.subjectType === 'additional_tasks'
    },
    usersWithEvents () {
      if (!this.editTask) {
        return []
      }
      let usersWithEvents = []
      this.editTask.subjects.forEach(subject => {
        if (subject.has_events) {
          if (subject.user_calendars.length > 0) {
            // Is in other calendar
            usersWithEvents = subject.user_calendars
          } else {
            // Is in own calendar
            usersWithEvents.push(subject.calendar_id)
          }
        }
      })
      return usersWithEvents
    },
    theDeletedItemId () {
      return this.$store.state.subjects.theDeletedItemId
    },
    isSubmittingTask () {
      return this.$store.state.subjects.isSubmittingTask
    }
  },
  watch: {
    editTask () {
      if (this.editTask) {
        this.name = this.editTask.name
        this.hours = this.editTask.hours_total
        this.calendars = []
        if (this.editTask.calendar_ids) {
          this.editTask.calendar_ids.forEach(calid => {
            this.calendars.push(calid)
          })
        }
        this.$refs.name.focus()
      }
    },
    defaultDuration (val) {
      this.removeErrors('defaultDuration')
    },
    theDeletedItemId () {
      if (this.theDeletedItemId === this.editTask.id) {
        this.close()
      }
    }
  },
  methods: {
    selectAllOnFocus (evt) {
      setTimeout(_ => {
        evt.target.select()
      }, 0)
    },
    getOtherCalendarName (calId) {
      return this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'other' ? this.allCalendarsInHash[calId].name : ''
    },
    setSelectedOtherCalendarId (calendarId) {
      this.selectedOtherCalendarId = calendarId
    },
    setSubjectType (type) {
      this.subjectType = type
    },
    setColor (color) {
      this.color = color
    },
    setSelectedUsers (users) {
      this.calendars = users.map(user => user.id)
    },
    setIsCreateAsSubject (boolean) {
      this.canSchedule = boolean
      if (!boolean) {
        this.setColor('#999999')
      } else {
        this.$nextTick(_ => {
          if (this.$refs.inputLessonCount) {
            this.$refs.inputLessonCount.focus()
          }
        })
      }
    },
    formValidation () {
      if (this.name.length < 1) {
        this.$set(this.formErrors, 'name', {
          da: 'Opgaven skal have et navn',
          en: 'Task must have a name',
          nb: 'Opgave må ha et navn'
        })
      }
      if (this.duration < 0) {
        this.$set(this.formErrors, 'duration', {
          da: 'Skal være 0 eller højere',
          en: 'Must be zero or greater',
          nb: 'Må være 0 eller høyere'
        })
      }
    },
    removeErrors (title) {
      this.$delete(this.formErrors, title)
    },
    createTask () {
      this.formValidation()
      if (Object.keys(this.formErrors).length > 0) {
        return
      }
      let belongsToCalendars = []
      let userCalendars = []
      let inUserCalendar = true
      if (this.selectedOtherCalendarId) {
        inUserCalendar = false
        belongsToCalendars = [this.selectedOtherCalendarId]
        userCalendars = this.calendars
      } else {
        belongsToCalendars = this.calendars
      }
      let newTask = {
        schoolYearId: this.activeSchoolYear.id,
        subjectType: this.subjectType,
        hours: this.hours,
        name: this.name,
        color: this.color,
        defaultDuration: this.duration,
        preparation: this.preparation,
        belongsToCalendars: belongsToCalendars,
        userCalendars: userCalendars,
        canSchedule: this.canSchedule,
        inUserCalendar: inUserCalendar
      }
      this.$emit('create', newTask)
      this.clearForm()
      this.$refs.name.focus()
    },
    updateTask () {
      this.formValidation()
      if (Object.keys(this.formErrors).length > 0) {
        return
      }
      const task = this.editTask
      const userCalIdsCannotDelete = this.usersWithEvents || [] // Sikrer, at der ikke bliver slettet brugere der ikke kan (også dem der har forladt skolen)
      const userCalendars = Array.from(new Set(this.calendars.concat(userCalIdsCannotDelete)))
      let belongsToCalendars = []
      if (!task.in_user_calendar) {
        belongsToCalendars = task.belongs_to_calendars[0]
      } else {
        belongsToCalendars = userCalendars // Fordi userCalendars allerede inkluderer både users og userCalIdsNotOnSchool
      }
      let updatedTask = {
        schoolId: this.editTask['school_id'],
        schoolYearId: this.editTask['school_year_id'],
        id: this.editTask['id'],
        name: this.name,
        color: this.color,
        hours: this.hours,
        defaultDuration: this.duration,
        preparation: this.preparation,
        belongsToCalendars: belongsToCalendars,
        userCalendars: userCalendars,
        inUserCalendar: this.editTask['in_user_calendar']
      }
      this.$store.dispatch('updateTask', updatedTask)
      this.$emit('close', updatedTask)
    },
    clearForm () {
      this.formErrors = []
      this.name = ''
      this.hours = 0
      this.duration = 45
      this.preparation = null
      this.color = '#999999'
      this.calendars = []
      this.selectedOtherCalendarId = null
    },
    close () {
      this.clearForm()
      this.canSchedule = true
      this.$emit('close')
    },
    deleteTask () {
      this.editTask['itemType'] = 'task'
      this.$store.commit('setItemToDelete', this.editTask)
      this.$store.commit('setConfirmDeletion', true)
    }
  },
  beforeMount () {
    this.locale = window.USER.language ? window.USER.language : 'da'
    if (this.activeFilter) {
      this.subjectType = this.activeFilter
    }
    if (this.editTask) {
      this.name = this.editTask.name
      this.hours = this.editTask.hours
      this.subjectType = this.editTask.subject_type
      this.color = this.editTask.color
      this.canSchedule = this.editTask.can_schedule
      this.duration = this.editTask.default_duration
      this.preparation = this.editTask.preparation * 1
      this.editTask.user_calendar_ids.forEach(calid => {
        this.calendars.push(calid)
      })
      if (!this.editTask.in_user_calendar) {
        this.selectedOtherCalendarId = this.editTask.belongs_to_calendars[0]
      }
    }
  },
  mounted () {
    setTimeout(_ => {
      this.$refs.name.focus()
    }, 500)
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";


.task-create__header {
  padding: 2.5rem 2rem;
  background: #ffffff;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.5s;
  border-radius: 4px 4px 0 0;
  color: #ffffff;
}

.task-create__header.task-create__header--create-subject h3,
.task-create__header.task-create__header--create-subject .task-create__close {
  color: #ffffff;
}


.task-create__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 8px;
  font-size: 15px;
  cursor: pointer;
  transition: color 0.25s, background-color 0.25s;
}

.task-create__modal {
  min-width: 550px;
  max-width: 550px;
  border-radius: 4px;
}

.task-create__body {
  padding: 2rem 2rem 0 2rem;
  max-height: calc(80vh);
  overflow-y: auto;
  border-radius: 4px;
}

.task-create__form .input-group {
  margin-bottom: 1.5rem;
  flex: 1;
}

.task-create__form .input-group:last-child {
  margin-right: 0;
}

.input-group__submit {
  display: flex;
  justify-content: space-between;
}

.input-group__flex {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.input-group__flex > .flex-1 {
  flex: 1;
}

.input-group input {
  background-color: #ffffff;
}

.lesson-count__edit input[type="number"] {
  margin: 0 0.25rem;
  text-align: center;
}

.button__delete-subject {
  color: var(--danger);
}

.expand-enter-active, .expand-leave-active {
  transition: transform 0.25s ease-in-out, opacity 0.25s ease-in-out;
}

.expand-enter, .expand-leave-to {
  transform: translateX(-0.5rem);
  opacity: 0;
}

.input-group label {
  letter-spacing: .075rem;
  text-transform: uppercase;
  font-size: 0.85rem;
  line-height: 1rem;
  font-weight: 700;
  display: block;
  color: #a2a3a6;
  margin-bottom: 6px;
}

.advanced-settings__link {
  cursor: pointer;
}

.advanced-settings__link:hover {
  text-decoration: underline;
}

.marg-1-right {
  margin-right: 1rem;
}

.create-as-subject {
  display: flex;
  align-items: center;
  user-select: none;
  cursor: pointer;
}

.create-as-subject__inner {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.create-as-subject__inner label {
  margin-bottom: 0;
  margin-left: 10px;
  cursor: pointer;
}

.create-as-subject__inner label:hover {
  color: var(--dark-blue);
}

.d-input {
  background-color: #fff;
  box-shadow: 0 2px 5px -1px rgb(50 50 93 / 25%), 0 1px 3px -1px rgb(0 0 0 / 30%);
  border: 1px solid #edf2f7;
  min-height: 32px;
}
</style>



// WEBPACK FOOTER //
// SubjectsDistributionTasksCreate.vue?6068498e